import type { OpeningHours } from '@/services/googlePlaces';

const TYPICAL_HOURS: Record<string, { open: number; close: number }> = {
  restaurant: { open: 11, close: 23 },
  cafe: { open: 7, close: 20 },
  coffee_shop: { open: 6, close: 20 },
  bakery: { open: 6, close: 18 },
  bar: { open: 17, close: 2 },
  night_club: { open: 21, close: 4 },
  pub: { open: 12, close: 1 },
  ice_cream_shop: { open: 10, close: 22 },
  food_court: { open: 10, close: 21 },
  meal_takeaway: { open: 10, close: 23 },
  fast_food_restaurant: { open: 7, close: 24 },

  museum: { open: 9, close: 17 },
  art_gallery: { open: 10, close: 18 },
  library: { open: 9, close: 20 },
  church: { open: 8, close: 18 },

  tourist_attraction: { open: 8, close: 20 },
  amusement_park: { open: 10, close: 21 },
  zoo: { open: 9, close: 17 },
  aquarium: { open: 9, close: 18 },
  botanical_garden: { open: 8, close: 18 },

  shopping_mall: { open: 10, close: 21 },
  store: { open: 9, close: 20 },
  book_store: { open: 9, close: 21 },
  clothing_store: { open: 10, close: 20 },
  market: { open: 7, close: 15 },

  spa: { open: 9, close: 21 },
  gym: { open: 6, close: 22 },
  movie_theater: { open: 12, close: 24 },
  bowling_alley: { open: 12, close: 23 },
  casino: { open: 0, close: 24 },

  park: { open: 6, close: 22 },
  national_park: { open: 6, close: 21 },
  playground: { open: 7, close: 21 },
  stadium: { open: 9, close: 22 },
};

function isHourInRange(hour: number, open: number, close: number): boolean {
  if (close > 24) close = close % 24;
  if (close <= open) {
    return hour >= open || hour < close;
  }
  if (close === 24) {
    return hour >= open;
  }
  return hour >= open && hour < close;
}

function matchTypicalHours(types: string[]): { open: number; close: number } | null {
  for (const type of types) {
    const normalised = type.toLowerCase().replace(/\s+/g, '_');
    if (TYPICAL_HOURS[normalised]) {
      return TYPICAL_HOURS[normalised];
    }
    for (const [key, hours] of Object.entries(TYPICAL_HOURS)) {
      if (normalised.includes(key) || key.includes(normalised)) {
        return hours;
      }
    }
  }
  return null;
}

/**
 * Returns the closing time (in decimal hours, possibly > 24 for overnight)
 * for the period that is currently active according to Google Places hours data.
 * Returns null if no matching period is found or data is unavailable.
 */
function getCurrentPeriodCloseHour(openingHours: OpeningHours | undefined): number | null {
  if (!openingHours?.periods?.length) return null;

  const now = new Date();
  const todayDay = now.getDay();
  const currentTotalMins = now.getHours() * 60 + now.getMinutes();

  for (const period of openingHours.periods) {
    if (!period.open || !period.close) continue;

    const openDay: number = period.open.day;
    const openTotalMins: number = period.open.hour * 60 + (period.open.minute || 0);
    const closeDay: number = period.close.day;
    const closeHourDecimal: number = period.close.hour + (period.close.minute || 0) / 60;
    const closeTotalMins: number = period.close.hour * 60 + (period.close.minute || 0);

    if (openDay === todayDay) {
      if (closeDay === todayDay) {
        // Normal same-day period
        if (currentTotalMins >= openTotalMins && currentTotalMins < closeTotalMins) {
          return closeHourDecimal;
        }
      } else {
        // Overnight: opens today, closes tomorrow — active from open time onward
        if (currentTotalMins >= openTotalMins) {
          return closeHourDecimal + 24; // +24 = next day
        }
      }
    }

    // Overnight period that opened yesterday and closes today
    const yesterday = (todayDay + 6) % 7;
    if (openDay === yesterday && closeDay === todayDay) {
      if (currentTotalMins < closeTotalMins) {
        return closeHourDecimal;
      }
    }
  }

  return null;
}

export interface InferredOpenStatus {
  likelyOpen: boolean;
  confidence: 'known' | 'inferred' | 'unknown';
  typicalHours?: { open: number; close: number };
  reason?: string;
}

/**
 * Infers whether a place will be open at arrival time (now + travelMins).
 *
 * Priority of data sources:
 * 1. `openNow` + `periods` — live snapshot from Google (highest confidence,
 *    used for fresh responses). openNow is the current gate; periods give the
 *    closing time for the arrival-time check.
 * 2. `periods` only (no openNow) — used for cached responses where openNow
 *    has been stripped to avoid staleness. The weekly schedule is stable.
 * 3. Typical hours by place type — low-confidence fallback when no hours data.
 */
export function inferOpenStatus(
  currentOpeningHours: OpeningHours | undefined,
  types: string[],
  currentHour: number,
  travelMins: number = 0
): InferredOpenStatus {
  const now = new Date();
  const nowTotalMins = now.getHours() * 60 + now.getMinutes();
  const arrivalTotalMins = nowTotalMins + travelMins;

  // ── Path 1: openNow is present (live response) ────────────────────────────
  if (currentOpeningHours?.openNow !== undefined) {
    if (!currentOpeningHours.openNow) {
      return { likelyOpen: false, confidence: 'known' };
    }

    // Open now — check arrival time against periods schedule
    if (travelMins > 0) {
      const closeHour = getCurrentPeriodCloseHour(currentOpeningHours);
      if (closeHour !== null && arrivalTotalMins >= closeHour * 60) {
        return {
          likelyOpen: false,
          confidence: 'known',
          reason: `Closes at ${formatHour(Math.floor(closeHour % 24))} — will be closed when you arrive`,
        };
      }
    }

    return { likelyOpen: true, confidence: 'known' };
  }

  // ── Path 2: periods present but openNow stripped (cached response) ────────
  // getCurrentPeriodCloseHour returns non-null iff there is an active period
  // right now (= currently open). Use that as the current-open gate, then
  // apply the same arrival-time check.
  if (currentOpeningHours?.periods?.length) {
    const closeHour = getCurrentPeriodCloseHour(currentOpeningHours);

    if (closeHour !== null) {
      // Currently inside an active period — check arrival time
      if (travelMins > 0 && arrivalTotalMins >= closeHour * 60) {
        return {
          likelyOpen: false,
          confidence: 'known',
          reason: `Closes at ${formatHour(Math.floor(closeHour % 24))} — will be closed when you arrive`,
        };
      }
      return { likelyOpen: true, confidence: 'known' };
    } else {
      // periods present but no active period right now — currently closed
      return { likelyOpen: false, confidence: 'known' };
    }
  }

  // ── Path 3: no hours data — typical hours fallback ────────────────────────
  const typical = matchTypicalHours(types);
  const arrivalHour = travelMins > 0
    ? Math.floor(arrivalTotalMins / 60) % 24
    : currentHour;

  if (!typical) {
    return {
      likelyOpen: arrivalHour >= 8 && arrivalHour < 22,
      confidence: 'unknown',
      reason: 'No hours data; assuming standard daytime hours',
    };
  }

  const open = isHourInRange(arrivalHour, typical.open, typical.close);
  const typeLabel = types[0]?.replace(/_/g, ' ') || 'this place';

  return {
    likelyOpen: open,
    confidence: 'inferred',
    typicalHours: typical,
    reason: open
      ? `Most ${typeLabel}s are open ${formatHour(typical.open)}-${formatHour(typical.close)}`
      : `Most ${typeLabel}s are closed at arrival time (typical: ${formatHour(typical.open)}-${formatHour(typical.close)})`,
  };
}

export function getOpenStatusSummary(
  currentOpeningHours: OpeningHours | undefined,
  types: string[],
  currentHour: number,
  travelMins: number = 0
): string {
  // Check "open now but closes before arrival" first
  if (travelMins > 0 && currentOpeningHours?.openNow === true) {
    const now = new Date();
    const arrivalTotalMins = now.getHours() * 60 + now.getMinutes() + travelMins;
    const closeHour = getCurrentPeriodCloseHour(currentOpeningHours);
    if (closeHour !== null && arrivalTotalMins >= closeHour * 60) {
      return `closes before arrival (${formatHour(Math.floor(closeHour % 24))})`;
    }
  }

  if (currentOpeningHours?.openNow === true) return 'open (confirmed)';
  if (currentOpeningHours?.openNow === false) return 'closed (confirmed)';

  const inferred = inferOpenStatus(currentOpeningHours, types, currentHour, travelMins);
  if (inferred.confidence === 'inferred') {
    return inferred.likelyOpen ? 'likely open (estimated)' : 'likely closed (estimated)';
  }
  return 'hours unknown';
}

function formatHour(h: number): string {
  if (h === 0 || h === 24) return '12am';
  if (h === 12) return '12pm';
  if (h > 24) h = h % 24;
  return h > 12 ? `${h - 12}pm` : `${h}am`;
}
